import { convertCentsToDollars } from '@apps/flows/activation/services/accountHelper'

const normalizeFeatureList = (value) => {
    if (Array.isArray(value)) {
        return value.filter(Boolean)
    }

    if (typeof value === 'string' && value.trim()) {
        return [value]
    }

    return []
}

export const normalizePlanByIdForLocale = (plan = {}, locale = 'en') => {
    // getPlanById returns `promotions[locale]` as an array of promo records — the
    // shape planMfeUtils unwraps with `promotions?.[0]`, and the same unwrap `info`
    // gets below. Read as an object it produced `{ id: undefined, bundles: [] }`,
    // which is truthy, so it satisfied every `selectedPlan?.promo` guard and seeded
    // an empty promo into the multi-promo arrays — the order summary then showed no
    // promo and no discount (MASC-17223).
    const localizedPromoRaw = plan?.promotions?.[locale]
    const localizedPromo = Array.isArray(localizedPromoRaw)
        ? localizedPromoRaw[0]
        : localizedPromoRaw
    const localizedInfoRaw = plan?.info?.[locale]
        || (
            plan?.info && typeof plan.info === 'object' && !Array.isArray(plan.info)
                ? plan.info
                : null
        )
    const localizedInfo = Array.isArray(localizedInfoRaw) ? localizedInfoRaw[0] : localizedInfoRaw
    const normalizedBundles = Array.isArray(localizedPromo?.bundles)
        ? localizedPromo.bundles.map((bundle = {}) => ({
            ...bundle,
            name: locale === 'fr'
                ? (bundle?.namefr || bundle?.name || '')
                : (bundle?.name || bundle?.namefr || '')
        }))
        : []

    return {
        id: `${plan?.id || ''}`,
        guid: plan?.guid,
        sluggedName: plan?.sluggedName,
        name: plan?.name?.[locale] || '',
        subtitle: plan?.subtitle?.[locale] || '',
        description: plan?.description?.[locale] || plan?.dataDescription?.[locale] || '',
        dataDescription: plan?.dataDescription?.[locale] || '',
        extraOffer: plan?.extraOffer?.[locale] || '',
        planData: plan?.planData?.[locale] || '',
        planExpiryDate: plan?.planExpiryDate?.[locale] || '',
        planRibbonContent: plan?.planRibbonContent?.[locale] || '',
        value: plan?.cost ?? plan?.originalPrice ?? 0,
        originalPrice: plan?.originalPrice ?? null,
        duration: plan?.numOfDays,
        features: normalizeFeatureList(plan?.features?.[locale]),
        includedPlanFeatures: normalizeFeatureList(plan?.includedPlanFeatures?.[locale]),
        highlightedFeatures: normalizeFeatureList(plan?.highlightedFeatures?.[locale]),
        offers: normalizeFeatureList(plan?.offers?.[locale]),
        recommended: Boolean(plan?.isRecommended),
        network: plan?.network || '',
        ltpSavings: plan?.extraOffer?.[locale] || plan?.planRibbonContent?.[locale] || '',
        info: localizedInfo
            ? {
                icon: localizedInfo?.icon || '',
                link: localizedInfo?.link || '',
                text: localizedInfo?.text || ''
            }
            : null,
        infoText: localizedInfo?.text || '',
        promo: localizedPromo
            ? {
                id: localizedPromo?.id,
                description: localizedPromo?.description,
                bundles: normalizedBundles
            }
            : null
    }
}

export const convertPlanPromoBundlesToDollars = (plan = {}) => {
    if (!plan?.promo || !Array.isArray(plan?.promo?.bundles)) {
        return plan
    }

    return {
        ...plan,
        promo: {
            ...plan.promo,
            bundles: plan.promo.bundles.map((bundle = {}) => ({
                ...bundle,
                fee: bundle?.fee != null ? convertCentsToDollars(bundle.fee) : bundle?.fee
            }))
        }
    }
}

// Copy-only fields. Identity (id/guid/sluggedName/pricePlanGuid), pricing
// (value/originalPrice/duration) and the MFE-only payload (commerce/options)
// deliberately stay on the stored plan: switching language must re-word the
// order summary, never change what gets quoted.
const LOCALIZED_COPY_FIELDS = [
    'name',
    'subtitle',
    'description',
    'dataDescription',
    'extraOffer',
    'planData',
    'planExpiryDate',
    'planRibbonContent',
    'features',
    'includedPlanFeatures',
    'highlightedFeatures',
    'offers',
    'ltpSavings',
    'info',
    'infoText'
]

const isEmptyCopy = (value) => (
    value == null
    || value === ''
    || (Array.isArray(value) && value.length === 0)
)

/**
 * Overlays the other locale's copy onto the plan already in the store.
 *
 * `storedPlan` is whatever the selection path dispatched — an MFE PlanCardVM
 * adapted by mfePlanToHostPlan, or a normalizePlanByIdForLocale result — so it
 * carries fields the localized source does not. Merging rather than replacing
 * keeps those intact; an empty localized field is skipped so a sparse locale
 * never blanks copy that was there.
 */
export const mergeLocalizedPlanCopy = (storedPlan, localizedPlan) => {
    if (!localizedPlan) return storedPlan

    const merged = { ...storedPlan }

    LOCALIZED_COPY_FIELDS.forEach((field) => {
        const value = localizedPlan[field]
        if (!isEmptyCopy(value)) merged[field] = value
    })

    // Promo bundles: take the name (what the order summary renders as the
    // discount row label) but keep the stored `fee`. The amounts in the store
    // have already been unit-normalised for the summary math (MASC-17223 /
    // MASC-17285) and re-seeding them from another source risks a double
    // conversion — a language toggle must not move a dollar figure.
    const localizedBundles = localizedPlan?.promo?.bundles
    if (storedPlan?.promo && Array.isArray(localizedBundles) && localizedBundles.length > 0) {
        merged.promo = {
            ...storedPlan.promo,
            description: localizedPlan.promo?.description || storedPlan.promo.description,
            bundles: (storedPlan.promo.bundles || []).map((bundle) => {
                const match = localizedBundles.find(
                    (localizedBundle) => String(localizedBundle?.id) === String(bundle?.id)
                )
                return match?.name ? { ...bundle, name: match.name } : bundle
            })
        }
    }

    return merged
}
